import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isToday,
  isSameDay,
  startOfDay,
} from 'date-fns';
import {
  ChevronLeft, ChevronRight, Ban, Loader2,
  Lock, Unlock, Plus, X, AlertTriangle, ArrowRight,
} from 'lucide-react';
import useDisponibility from '../../hooks/useDisponibility';
import './weekView.css';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS HOLIDAYS
// ─────────────────────────────────────────────────────────────────────────────

function hijriToGregorian(hYear, hMonth, hDay) {
  const jd =
    Math.floor((11 * hYear + 3) / 30) + 354 * hYear + 30 * hMonth -
    Math.floor((hMonth - 1) / 2) + hDay + 1948440 - 385;
  let l = jd + 68569;
  const n = Math.floor((4 * l) / 146097);
  l = l - Math.floor((146097 * n + 3) / 4);
  const i = Math.floor((4000 * (l + 1)) / 1461001);
  l = l - Math.floor((1461 * i) / 4) + 31;
  const j = Math.floor((80 * l) / 2447);
  const day = l - Math.floor((2447 * j) / 80);
  l = Math.floor(j / 11);
  const month = j + 2 - 12 * l;
  const year = 100 * (n - 49) + i + l;
  return new Date(year, month - 1, day);
}

function gregorianToHijriYear(gDate) {
  return Math.floor((gDate.getFullYear() - 622) * (33 / 32)) + 1;
}

function getIslamicHoliday(gYear, hMonth, hDay, offsetDays = 0) {
  const hYear = gregorianToHijriYear(new Date(gYear, 0, 1));
  const dates = [];
  for (let y = hYear - 1; y <= hYear + 1; y++) {
    const d = hijriToGregorian(y, hMonth, hDay);
    const shifted = new Date(d);
    shifted.setDate(shifted.getDate() + offsetDays);
    if (shifted.getFullYear() === gYear) dates.push(shifted);
  }
  return dates;
}

function getTunisianHolidays(year, t) {
  const holidays = [];
  const fixed = [
    { month: 0,  day: 1,  key: 'newYear' },
    { month: 0,  day: 14, key: 'revolutionDay' },
    { month: 2,  day: 20, key: 'independenceDay' },
    { month: 3,  day: 9,  key: 'martyrsDay' },
    { month: 4,  day: 1,  key: 'labourDay' },
    { month: 6,  day: 25, key: 'republicDay' },
    { month: 7,  day: 13, key: 'womenDay' },
    { month: 9,  day: 15, key: 'evacuationDay' },
    { month: 11, day: 17, key: 'decRevolution' },
  ];
  fixed.forEach(({ month, day, key }) =>
    holidays.push({ date: new Date(year, month, day), name: t(`holidays.${key}`) })
  );
  getIslamicHoliday(year, 10, 1).forEach((d)    => holidays.push({ date: d, name: t('holidays.eidAlFitr1') }));
  getIslamicHoliday(year, 10, 1, 1).forEach((d) => holidays.push({ date: d, name: t('holidays.eidAlFitr2') }));
  getIslamicHoliday(year, 12, 10).forEach((d)   => holidays.push({ date: d, name: t('holidays.eidAlAdha1') }));
  getIslamicHoliday(year, 12, 10, 1).forEach((d)=> holidays.push({ date: d, name: t('holidays.eidAlAdha2') }));
  getIslamicHoliday(year, 1, 1).forEach((d)     => holidays.push({ date: d, name: t('holidays.islamicNewYear') }));
  getIslamicHoliday(year, 3, 12).forEach((d)    => holidays.push({ date: d, name: t('holidays.mawlid') }));
  return holidays;
}

const holidayCache = {};
function getHolidaysForYear(year, t, lang) {
  const cacheKey = `${year}_${lang}`;
  if (!holidayCache[cacheKey]) holidayCache[cacheKey] = getTunisianHolidays(year, t);
  return holidayCache[cacheKey];
}
function getHolidaysForDate(date, t, lang) {
  return getHolidaysForYear(date.getFullYear(), t, lang).filter((h) => isSameDay(h.date, date));
}
function isPublicHoliday(date, t, lang) { return getHolidaysForDate(date, t, lang).length > 0; }
function getHolidayNames(date, t, lang) {
  return getHolidaysForDate(date, t, lang).map((h) => h.name).join(' · ');
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS SLOTS / EVENTS
// ─────────────────────────────────────────────────────────────────────────────

function hasEventOnDay(events, date) {
  const dayStart = startOfDay(date);
  return events.some((e) => {
    const start = startOfDay(new Date(e.startDate));
    const end   = startOfDay(new Date(e.endDate));
    return dayStart >= start && dayStart <= end;
  });
}

function getEventsOnDay(events, date) {
  const dayStart = startOfDay(date);
  return events.filter((e) => {
    const start = startOfDay(new Date(e.startDate));
    const end   = startOfDay(new Date(e.endDate));
    return dayStart >= start && dayStart <= end;
  });
}

function hasSlotOnDate(rawSlots, date) {
  const target = format(date, 'yyyy-MM-dd');
  return rawSlots.some((slot) => String(slot.startTime ?? '').substring(0, 10) === target);
}

function getSlotsForDate(rawSlots, date) {
  const target = format(date, 'yyyy-MM-dd');
  return rawSlots.filter((slot) => String(slot.startTime ?? '').substring(0, 10) === target);
}

// ─────────────────────────────────────────────────────────────────────────────
// BUSINESS ERROR MODAL
// ─────────────────────────────────────────────────────────────────────────────

function BusinessErrorModal({ open, title, message, onClose, t }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--smart" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <p className="modal__title">{title}</p>
            <p className="modal__date">{t('modal.businessRule')}</p>
          </div>
          <button className="modal__close-btn" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>
        <div className="modal__section" style={{ borderBottom: 'none' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertTriangle size={18} color="#f97316" style={{ marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#374151' }}>{message}</p>
          </div>
          <div className="modal__actions" style={{ marginTop: 18 }}>
            <button className="modal__btn modal__btn--confirm" type="button" onClick={onClose}>
              {t('modal.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DAY MANAGEMENT MODAL
// ─────────────────────────────────────────────────────────────────────────────

function DayManagementModal({
  date, blockedSlots, canBlockThisDate,
  onClose, onBlock, onUnblock, loading, onBusinessError, t,
}) {
  const [showAddForm, setShowAddForm]   = useState(false);
  const [startTime, setStartTime]       = useState('09:00');
  const [endTime, setEndTime]           = useState('17:00');
  const [reason, setReason]             = useState('');
  const [error, setError]               = useState(null);
  const [unblockingId, setUnblockingId] = useState(null);

  const hasBlocked = blockedSlots.length > 0;

  const formatRange = (startISO, endISO) => {
    const fmt = (iso) => {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    return `${fmt(startISO)} – ${fmt(endISO)}`;
  };

  const handleUnblock = async (slot) => {
    setUnblockingId(slot.id);
    try { await onUnblock(slot.id); }
    catch (err) { console.error('[Modal] unblock error:', err); }
    finally { setUnblockingId(null); }
  };

  const handleOpenAddForm = () => {
    if (!canBlockThisDate) {
      onBusinessError?.(t('modal.errors.requireAvailability'));
      return;
    }
    setShowAddForm(true);
  };

  const handleBlock = async () => {
    if (!canBlockThisDate) {
      onBusinessError?.(t('modal.errors.requireAvailability'));
      return;
    }
    if (!startTime || !endTime) { setError(t('modal.errors.fillBothTimes')); return; }
    if (startTime >= endTime)   { setError(t('modal.errors.endAfterStart')); return; }

    setError(null);
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      await onBlock({
        startTime: `${dateStr}T${startTime}:00`,
        endTime:   `${dateStr}T${endTime}:00`,
        reason:    reason.trim() || undefined,
      });
      setShowAddForm(false);
      setStartTime('09:00');
      setEndTime('17:00');
      setReason('');
    } catch (err) {
      setError(err?.message ?? t('modal.errors.blockError'));
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal modal--smart" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <p className="modal__title">{t('modal.manageDay')}</p>
            <p className="modal__date">{format(date, 'EEEE, d MMMM yyyy')}</p>
          </div>
          <button className="modal__close-btn" onClick={onClose} type="button" disabled={loading}>
            <X size={18} />
          </button>
        </div>

        {hasBlocked && (
          <div className="modal__section">
            <p className="modal__section-title">
              <Lock size={13} style={{ display: 'inline', marginRight: 5 }} />
              {t('modal.blockedSlots')}
            </p>
            <div className="modal__blocked-list">
              {blockedSlots.map((slot) => (
                <div key={slot.id} className="modal__blocked-item">
                  <span className="modal__blocked-range">{formatRange(slot.startTime, slot.endTime)}</span>
                  <button
                    className="modal__unblock-btn"
                    type="button"
                    disabled={!!unblockingId || loading}
                    onClick={() => handleUnblock(slot)}
                  >
                    {unblockingId === slot.id
                      ? <><Loader2 size={13} className="modal__spin" /> {t('modal.unblocking')}</>
                      : <><Unlock size={13} /> {t('modal.unblock')}</>
                    }
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!showAddForm ? (
          <button className="modal__add-btn" type="button" disabled={loading} onClick={handleOpenAddForm}>
            <Plus size={14} />
            {t('modal.blockSlot')}
          </button>
        ) : (
          <div className="modal__section modal__section--form">
            <p className="modal__section-title">
              <Ban size={13} style={{ display: 'inline', marginRight: 5 }} />
              {t('modal.newBlock')}
            </p>
            <div className="modal__row">
              <div className="modal__field">
                <label className="modal__label">{t('modal.startTime')}</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="modal__input" disabled={loading} />
              </div>
              <div className="modal__field">
                <label className="modal__label">{t('modal.endTime')}</label>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="modal__input" disabled={loading} />
              </div>
            </div>
            <div className="modal__field">
              <label className="modal__label">{t('modal.reason')}</label>
              <input
                type="text"
                placeholder={t('modal.reasonPlaceholder')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="modal__input"
                disabled={loading}
              />
            </div>
            {error && <div className="modal__error"><p>{error}</p></div>}
            <div className="modal__actions">
              <button
                onClick={() => { setShowAddForm(false); setError(null); }}
                className="modal__btn modal__btn--cancel"
                type="button"
                disabled={loading}
              >
                {t('modal.cancel')}
              </button>
              <button onClick={handleBlock} className="modal__btn modal__btn--confirm" type="button" disabled={loading}>
                {loading
                  ? <><Loader2 size={14} className="modal__spin" /> {t('modal.blocking')}</>
                  : t('modal.block')
                }
              </button>
            </div>
          </div>
        )}

        {!hasBlocked && !showAddForm && (
          <p className="modal__empty-hint">{t('modal.noBlockedSlots')}</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DAY ROW  (list item — replaces the old grid column)
// ─────────────────────────────────────────────────────────────────────────────

function DayRow({ date, events, blockedSlots, rawSlots, onDayClick, t, lang }) {
  const today        = isToday(date);
  const isHoliday    = isPublicHoliday(date, t, lang);
  const holidayLabel = getHolidayNames(date, t, lang);

  const dayBlocked  = blockedSlots.filter((s) => isSameDay(new Date(s.date), date));
  const dayEvents   = getEventsOnDay(events, date);
  const daySlots    = getSlotsForDate(rawSlots, date);

  const isBlocked   = dayBlocked.length > 0;
  const hasEvent    = dayEvents.length > 0;
  const isAvailable = daySlots.length > 0;
  const dateLocale = lang || 'en';

  let status = 'unavailable';
  if (isBlocked)       status = 'blocked';
  else if (isHoliday)  status = 'holiday';
  else if (hasEvent)   status = 'event';
  else if (isAvailable) status = 'available';

  const formatHM = (iso) => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const description = (() => {
    if (status === 'blocked') {
      return t('weekView.list.blockedDesc', { defaultValue: 'Click to change state' });
    }
    if (status === 'holiday') {
      return holidayLabel;
    }
    if (status === 'event') {
      const ev = dayEvents[0];
      if (ev?.startDate && ev?.endDate) {
        return t('weekView.list.eventDesc', {
          start: format(new Date(ev.startDate), 'h a').toLowerCase(),
          end: format(new Date(ev.endDate), 'h a').toLowerCase(),
          defaultValue: `Event from ${format(new Date(ev.startDate), 'h a').toLowerCase()} to ${format(new Date(ev.endDate), 'h a').toLowerCase()}`,
        });
      }
      return ev?.title || t('weekView.pills.event');
    }
    if (status === 'available') {
      return t('weekView.list.availableDesc', { defaultValue: 'Available according to calendar' });
    }
    return t('weekView.clickToSchedule');
  })();

  const pillLabel = {
    available: t('weekView.pills.available'),
    holiday: t('weekView.pills.holiday'),
    event: t('weekView.pills.event'),
    blocked: t('weekView.pills.blocked'),
    unavailable: t('weekView.pills.unavailable'),
  }[status];

  return (
    <li
      className={`wvl-row wvl-row--${status}${today ? ' wvl-row--today' : ''}`}
      onClick={() => onDayClick(date, dayBlocked)}
      role="button"
      tabIndex={0}
      title={isHoliday ? holidayLabel : undefined}
      onKeyDown={(e) => e.key === 'Enter' && onDayClick(date, dayBlocked)}
    >
      <div className="wvl-row__date">
        <span className="wvl-row__dow">{new Intl.DateTimeFormat(dateLocale, { weekday: 'short' }).format(date)}</span>
        <span className="wvl-row__num">{format(date, 'd')}</span>
        <span className="wvl-row__month">{new Intl.DateTimeFormat(dateLocale, { month: 'short' }).format(date).toUpperCase()}</span>
      </div>

      <div className="wvl-row__body">
        <span className={`wvl-pill wvl-pill--${status}`}>{pillLabel}</span>
        <p className="wvl-row__desc">{description}</p>
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WEEK VIEW (weekly list)
// ─────────────────────────────────────────────────────────────────────────────

export default function WeekView({ medecinId, events = [], rawSlots = [], slotDuration = 30, onDayClick }) {
  const { t, i18n } = useTranslation('availability');

  const [weekStart, setWeekStart]             = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedDate, setSelectedDate]       = useState(null);
  const [selectedBlocked, setSelectedBlocked] = useState([]);
  const [showModal, setShowModal]             = useState(false);
  const [blockedDays, setBlockedDays]         = useState([]);
  const [blockLoading, setBlockLoading]       = useState(false);
  const [businessError, setBusinessError]     = useState('');

  const { loadSlots, blockRange, deleteSlot, loadBlockedSlots } = useDisponibility({
    medecinId,
    date: selectedDate ?? new Date(),
  });

  useEffect(() => {
    if (!medecinId) return;
    let cancelled = false;

    loadBlockedSlots(medecinId)
      .then((action) => {
        if (cancelled) return;
        const slots = action?.payload?.data?.slots ?? [];
        setBlockedDays(slots.map((s) => ({
          id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime,
        })));
      })
      .catch((err) => console.error('[WeekView] loadBlockedSlots error:', err));

    return () => { cancelled = true; };
  }, [medecinId]); // eslint-disable-line react-hooks/exhaustive-deps

  const days      = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd   = addDays(weekStart, 6);
  const rangeFormatter = new Intl.DateTimeFormat(i18n.language || 'en', {
    day: 'numeric',
    month: 'long',
  });

  const getBlockedSlotsForDate = useCallback(
    (d) => blockedDays.filter((b) => isSameDay(new Date(b.date), d)),
    [blockedDays]
  );

  const canBlockDate = useCallback(
    (d) => hasSlotOnDate(rawSlots, d),
    [rawSlots]
  );

  const handleDayClick = useCallback(
    (date, dayBlocked) => {
      setSelectedDate(date);
      setSelectedBlocked(dayBlocked);
      setShowModal(true);

      if (onDayClick) {
        const lang   = i18n.language;
        const status =
          dayBlocked.length > 0               ? 'blocked'     :
          isPublicHoliday(date, t, lang)      ? 'holiday'     :
          hasEventOnDay(events, date)          ? 'event'       :
          hasSlotOnDate(rawSlots, date)        ? 'available'   : 'unavailable';

        onDayClick(date, status, {
          blockedSlots: dayBlocked,
          events:  getEventsOnDay(events, date),
          slots:   getSlotsForDate(rawSlots, date),
          holiday: isPublicHoliday(date, t, lang) ? getHolidayNames(date, t, lang) : null,
        });
      }
    },
    [onDayClick, events, rawSlots, t, i18n.language]
  );

  const handleBlock = useCallback(
    async ({ startTime, endTime, reason }) => {
      if (!selectedDate || !canBlockDate(selectedDate)) {
        setBusinessError(t('modal.errors.requireAvailability'));
        return;
      }
      setBlockLoading(true);
      try {
        const optimisticId = `optimistic-${Date.now()}`;
        setBlockedDays((prev) => [...prev, { id: optimisticId, date: new Date(startTime), startTime, endTime }]);
        setSelectedBlocked((prev) => [...prev, { id: optimisticId, date: new Date(startTime), startTime, endTime }]);

        await blockRange({ medecinId, startTime, endTime, reason });

        const action = await loadBlockedSlots(medecinId);
        const freshSlots = action?.payload?.data?.slots ?? [];
        const mapped = freshSlots.map((s) => ({ id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime }));
        setBlockedDays(mapped);
        if (selectedDate) setSelectedBlocked(mapped.filter((b) => isSameDay(b.date, selectedDate)));

        await loadSlots(medecinId, startTime.split('T')[0]);
      } catch (err) {
        setBlockedDays((prev) => prev.filter((b) => !String(b.id).startsWith('optimistic-')));
        setSelectedBlocked((prev) => prev.filter((b) => !String(b.id).startsWith('optimistic-')));
        throw err;
      } finally {
        setBlockLoading(false);
      }
    },
    [selectedDate, canBlockDate, medecinId, blockRange, loadBlockedSlots, loadSlots, t]
  );

  const handleUnblock = useCallback(
    async (slotId) => {
      setBlockedDays((prev) => prev.filter((b) => b.id !== slotId));
      setSelectedBlocked((prev) => prev.filter((b) => b.id !== slotId));
      try {
        await deleteSlot(slotId);
        const action = await loadBlockedSlots(medecinId);
        const freshSlots = action?.payload?.data?.slots ?? [];
        const mapped = freshSlots.map((s) => ({ id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime }));
        setBlockedDays(mapped);
        if (selectedDate) setSelectedBlocked(mapped.filter((b) => isSameDay(b.date, selectedDate)));
      } catch (err) {
        console.error('[WeekView] unblock error:', err);
        const action = await loadBlockedSlots(medecinId);
        const freshSlots = action?.payload?.data?.slots ?? [];
        const mapped = freshSlots.map((s) => ({ id: s.id, date: new Date(s.startTime), startTime: s.startTime, endTime: s.endTime }));
        setBlockedDays(mapped);
        if (selectedDate) setSelectedBlocked(mapped.filter((b) => isSameDay(b.date, selectedDate)));
        throw err;
      }
    },
    [deleteSlot, loadBlockedSlots, medecinId, selectedDate]
  );

  return (
    <>
      <div className="wvl-card">
        <div className="wvl-header">
          <button
            className="wvl-nav-btn"
            onClick={() => setWeekStart(subWeeks(weekStart, 1))}
            aria-label="Previous week"
            type="button"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="wvl-header__info">
            <p className="wvl-header__title">{t('weekView.list.title', { defaultValue: 'Weekly calendar' })}</p>
            <p className="wvl-header__subtitle">{t('weekView.list.subtitle', { defaultValue: 'Manage your availability by day' })}</p>
          </div>

          <button
            className="wvl-nav-btn"
            onClick={() => setWeekStart(addWeeks(weekStart, 1))}
            aria-label="Next week"
            type="button"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="wvl-range">
          <span className="wvl-range__label">{t('weekView.list.from', { defaultValue: 'FROM' })}</span>
          <span className="wvl-range__value">{rangeFormatter.format(weekStart)}</span>
          <ArrowRight size={13} className="wvl-range__arrow" />
          <span className="wvl-range__label">{t('weekView.list.to', { defaultValue: 'To' })}</span>
          <span className="wvl-range__value">{rangeFormatter.format(weekEnd)}</span>
        </div>

        <div className="wvl-list-wrap">
          <ul className="wvl-list">
            {days.map((date) => (
              <DayRow
                key={date.toISOString()}
                date={date}
                events={events}
                blockedSlots={blockedDays}
                rawSlots={rawSlots}
                onDayClick={handleDayClick}
                t={t}
                lang={i18n.language}
              />
            ))}
          </ul>
        </div>
      </div>

      {showModal && selectedDate && (
        <DayManagementModal
          date={selectedDate}
          blockedSlots={getBlockedSlotsForDate(selectedDate)}
          canBlockThisDate={canBlockDate(selectedDate)}
          onClose={() => setShowModal(false)}
          onBlock={handleBlock}
          onUnblock={handleUnblock}
          loading={blockLoading}
          onBusinessError={(msg) => setBusinessError(msg)}
          t={t}
        />
      )}

      <BusinessErrorModal
        open={!!businessError}
        title={t('businessError.title')}
        message={businessError}
        onClose={() => setBusinessError('')}
        t={t}
      />
    </>
  );
}